import { Delete } from '@assets/icons/Delete';
import { ImageArrowUp } from '@assets/icons/ImageArrowUp';
import { ErrorFrame } from '@components/utility/forms';
import { InputField } from '@components/utility/inputs';
import { Select } from '@components/utility/selects';
import { useComponentDetailContext } from '@contentManagement/contexts';
import { ComponentDetailEditBar, ComponentDetailViewBar } from '@contentManagement/routes/dashboard/ComponentDetail';
import { ComponentDetailSchema } from '@contentManagement/schemas';
import {
    useDeleteManagementOverviewBannerImageMutation,
    useGetManagementOverviewBannerQuery,
    usePatchManagementOverviewBannerMutation,
    usePostManagementOverviewBannerImageMutation,
} from '@contentManagement/services';
import { ManagementOverviewBanner } from '@contentManagement/types';
import { useAppDispatch } from '@hooks/generic';
import { DirtyFormikListener } from '@hooks/useDirtyFormik';
import { Box, IconButton, Skeleton, Stack, styled, Typography } from '@mui/material';
import { setTitle } from '@store/appSlice';
import { handleError, toaster } from '@utils/common';
import clsx from 'clsx';
import { FormikProvider, useFormik } from 'formik';
import { useEffect, useMemo } from 'react';
import PlaceholderImage from 'src/assets/placeholder.png';

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const backgroundOptions = [
    { label: 'Light Blue', value: 'blue' },
    { label: 'Dark Blue', value: 'dark' },
];

const emptyInitialValues: ManagementOverviewBanner = {
    image: '',
    header_title: '',
    header_text: '',
    button_text: '',
    button_link: '',
    background: 'dark',
};

export const ComponentDetail = () => {
    //#region State
    const [editMode, setEditMode] = useComponentDetailContext().editModeState;
    //#endregion

    //#region Hooks
    const dispatch = useAppDispatch();

    const { data, isFetching: isFetchingBanner } = useGetManagementOverviewBannerQuery();
    const [patchBanner] = usePatchManagementOverviewBannerMutation();
    const [uploadBannerImage] = usePostManagementOverviewBannerImageMutation();
    const [deleteBannerImage] = useDeleteManagementOverviewBannerImageMutation();

    const initialValues = useMemo(() => data || emptyInitialValues, [data]);
    const formik = useFormik<Omit<ManagementOverviewBanner, 'image'> & { image?: string | File }>({
        initialValues,
        enableReinitialize: true,
        validationSchema: ComponentDetailSchema,
        onSubmit: async (values, helpers) => {
            const { image: _image, ...bannerValues } = values;

            const imagePromise =
                typeof values.image === 'object' && values.image instanceof File
                    ? (() => {
                          const formData = new FormData();
                          formData.append('image', values.image);
                          return uploadBannerImage(formData).unwrap();
                      })()
                    : typeof formik.initialValues.image === 'string' && !values.image
                    ? (() => {
                          return deleteBannerImage().unwrap();
                      })()
                    : Promise.resolve();

            const bannerPatchPromise = patchBanner(bannerValues).unwrap();

            const [imageUploadResult, bannerPatchResult] = await Promise.allSettled([imagePromise, bannerPatchPromise]);

            if (imageUploadResult.status === 'rejected') {
                handleError('Failed to upload image. Please try again.', imageUploadResult.reason);
            }

            if (bannerPatchResult.status === 'rejected') {
                handleError('Failed to update component. Please try again.', bannerPatchResult.reason);
            }

            if (imageUploadResult.status === 'rejected' || bannerPatchResult.status === 'rejected') {
                helpers.setSubmitting(false);
                return;
            }

            toaster.success({
                title: 'Success',
                msg: 'Component updated successfully',
            });
            setEditMode(false);
            helpers.setSubmitting(false);
            helpers.resetForm({ values });
        },
    });
    //#endregion

    //#region Processed Data
    const imagePreviewSrc =
        typeof formik.values.image === 'string'
            ? formik.values.image
            : formik.values.image
            ? URL.createObjectURL(formik.values.image)
            : PlaceholderImage;

    const imageLabel =
        typeof formik.values.image === 'string'
            ? formik.values.image.split('/').pop() || 'Current Image'
            : formik.values.image?.name || 'No Image';
    //#endregion

    //#region Effects
    useEffect(() => {
        dispatch(setTitle('Content Management - Overview Banner'));
    }, [dispatch]);
    //#endregion

    return (
        <FormikProvider value={formik}>
            <DirtyFormikListener />
            <form onSubmit={formik.handleSubmit} noValidate>
                <Stack
                    direction="row"
                    justifyContent="flex-end"
                    border={1}
                    borderRadius="14px"
                    bgcolor="#fff"
                    borderColor="rgba(0,0,0,0.1)"
                    py="13px"
                    px="30px"
                >
                    {!editMode ? <ComponentDetailViewBar isFetching={isFetchingBanner} /> : <ComponentDetailEditBar />}
                </Stack>
                <Box
                    border={1}
                    borderRadius="14px"
                    bgcolor="#fff"
                    borderColor="rgba(0,0,0,0.1)"
                    px="40px"
                    py="44px"
                    mt="25px"
                >
                    <Typography fontFamily="museo-sans" fontSize="24px" letterSpacing="-1px">
                        Edit Component
                    </Typography>
                    <Typography fontSize="16px" letterSpacing={0}>
                        Manage the content displayed in the Victron Professional Website Overview banner.
                    </Typography>
                    <Stack direction="column" gap="26px" mt="46px">
                        <ErrorFrame error={!!formik.errors.image} helperText={formik.errors.image}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                gap="20px"
                                component={formik.isSubmitting || isFetchingBanner ? Skeleton : 'div'}
                                variant="rounded"
                            >
                                <img
                                    src={imagePreviewSrc}
                                    alt="Component Preview"
                                    width={218}
                                    height={125}
                                    className={clsx('rounded-lg', { grayscale: !editMode })}
                                />
                                <Typography
                                    fontSize="19px"
                                    letterSpacing={0.2}
                                    maxWidth="200px"
                                    textOverflow="ellipsis"
                                    whiteSpace="nowrap"
                                    overflow="hidden"
                                >
                                    {imageLabel}
                                </Typography>
                                <Stack direction="row" alignItems="center" gap="10px">
                                    <IconButton
                                        color="primary"
                                        className="border border-solid border-primary p-[4px]"
                                        component="label"
                                        disabled={!editMode}
                                    >
                                        <ImageArrowUp />
                                        <VisuallyHiddenInput
                                            type="file"
                                            accept="image/*"
                                            onChange={(event) => {
                                                const file = event.target.files?.[0] ?? null;
                                                formik.setFieldValue(
                                                    'image',
                                                    file && file.type.startsWith('image/') ? file : null
                                                );
                                            }}
                                        />
                                    </IconButton>
                                    <IconButton
                                        color="error"
                                        className="border border-solid border-primary p-[4px]"
                                        onClick={() => formik.setFieldValue('image', null)}
                                        disabled={!editMode}
                                    >
                                        <Delete />
                                    </IconButton>
                                </Stack>
                            </Stack>
                        </ErrorFrame>
                        <InputField
                            buffered
                            label="Header Title"
                            fullWidth
                            required
                            name="header_title"
                            helperText="Enter a short, attention-grabbing headline. (Max 100 characters)"
                            disabled={!editMode}
                            debounceMs={0}
                            loading={formik.isSubmitting || isFetchingBanner}
                        />
                        <InputField
                            buffered
                            label="Header Text"
                            fullWidth
                            required
                            name="header_text"
                            helperText="Add a brief description or subheading to support the title. (Max 150 characters)"
                            multiline
                            minRows={2}
                            disabled={!editMode}
                            debounceMs={0}
                            loading={formik.isSubmitting || isFetchingBanner}
                        />
                        <InputField
                            buffered
                            label="Button Text"
                            fullWidth
                            required
                            name="button_text"
                            helperText='The action label displayed on the button (e.g., "Read More", "Get Started"). (Max 30 characters)'
                            disabled={!editMode}
                            debounceMs={0}
                            loading={formik.isSubmitting || isFetchingBanner}
                        />
                        <InputField
                            buffered
                            label="Button Link"
                            fullWidth
                            required
                            name="button_link"
                            helperText="The destination URL. Use https:// for external sites or a relative path like /overview."
                            disabled={!editMode}
                            debounceMs={0}
                            loading={formik.isSubmitting || isFetchingBanner}
                        />
                        <Select
                            name="background"
                            label="Background Color"
                            fullWidth
                            items={backgroundOptions}
                            value={formik.values.background}
                            onChange={(event) => formik.setFieldValue('background', event.target.value)}
                            error={formik.touched.background && !!formik.errors.background}
                            helperText={
                                formik.touched.background && !!formik.errors.background
                                    ? formik.errors.background
                                    : 'Choose the background color block for this section.'
                            }
                            type="radio"
                            size="smallest"
                            disabled={!editMode}
                            loading={formik.isSubmitting || isFetchingBanner}
                            loadingType="skeleton"
                        />
                    </Stack>
                </Box>
            </form>
        </FormikProvider>
    );
};
